import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import { createGame, step, WIDTH, HEIGHT } from '../../lib/arcade/asteroidsEngine';
import { render as renderFrame } from '../../lib/arcade/asteroidsRenderer';
import { createAsteroidsSound } from '../../lib/arcade/asteroidsSound';
import { createGameLoop } from '../../lib/arcade/gameLoop';
import ArcadeLeaderboard from './leaderboard';

/** Hidden-arcade Asteroids UI (C): controls, loop, HUD, sound and leaderboard. */
export const GAME_KEY = 'asteroids';

const HUD_RESERVED_PX = 170;
const TOUCH_CONTROLS_PX = 90;
const CONTROL_KEYS = {
    ArrowLeft: 'left', KeyA: 'left',
    ArrowRight: 'right', KeyD: 'right',
    ArrowUp: 'thrust', KeyW: 'thrust',
    Space: 'fire',
};
const KEY_START = ['Space', 'Enter'];

function isTouchDevice() {
    if (typeof window === 'undefined') {
        return false;
    }
    return 'ontouchstart' in window || (window.innerWidth || 0) < 768;
}

@observer
class Asteroids extends Component {
    constructor(props) {
        super(props);
        const muted = !!util.get('arcade.muted');
        this.state = {
            screen: 'title',
            engineStatus: 'ready',
            score: 0,
            wave: 1,
            lives: 3,
            scale: 1,
            isTouch: isTouchDevice(),
            submitting: false,
            muted,
        };
        this.sound = createAsteroidsSound({ muted });
        this.loop = createGameLoop({ onTick: this.tick, onFrame: this.afterFrame });
        this.game = null;
        this.held = {
            left: false, right: false, thrust: false, fire: false,
        };
        this.frame = 0;
        this.startedAt = 0;
        this.unmounted = false;
        this.canvasRef = null;
        this.wrapRef = null;
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('resize', this.fitCanvas);
        window.addEventListener('blur', this.pause);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.fitCanvas();
        this.drawIdle();
        this.loadScores();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.scale !== this.state.scale) {
            const ctx = this.getCtx();
            if (ctx && this.game) {
                renderFrame(ctx, this.game, this.frame);
            } else {
                this.drawIdle();
            }
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.loop.stop();
        this.sound.dispose();
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('resize', this.fitCanvas);
        window.removeEventListener('blur', this.pause);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    loadScores = async () => {
        const { arcadeScoreStore } = this.props.stores;
        if (arcadeScoreStore?.loadLeaderboard) {
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
    }

    getCtx() {
        return this.canvasRef ? this.canvasRef.getContext('2d') : null;
    }

    fitCanvas = () => {
        const wrapWidth = this.wrapRef ? this.wrapRef.clientWidth : WIDTH;
        const reserved = HUD_RESERVED_PX + (this.state.isTouch ? TOUCH_CONTROLS_PX : 0);
        const availableHeight = Math.max(HEIGHT, (window.innerHeight || HEIGHT) - reserved);
        const raw = Math.min(wrapWidth / WIDTH, availableHeight / HEIGHT);
        const scale = Math.max(1, Math.floor(raw * 2) / 2);
        if (scale !== this.state.scale) {
            this.setState({ scale });
        }
    }

    drawIdle() {
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, createGame({ rng: () => 0.25 }), 0);
        }
    }

    startGame = () => {
        this.sound.unlock();
        this.game = createGame();
        this.held = {
            left: false, right: false, thrust: false, fire: false,
        };
        this.frame = 0;
        this.startedAt = Date.now();
        this.setState({
            screen: 'game',
            engineStatus: 'ready',
            score: 0,
            wave: 1,
            lives: 3,
            submitting: false,
        }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    pause = () => {
        if (this.state.screen !== 'game') {
            return;
        }
        this.loop.stop();
        this.held = {
            left: false, right: false, thrust: false, fire: false,
        };
        this.setState({ screen: 'paused' });
    }

    resume = () => {
        if (this.state.screen !== 'paused') {
            return;
        }
        this.sound.unlock();
        this.setState({ screen: 'game' }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    togglePause = () => {
        if (this.state.screen === 'game') {
            this.pause();
        } else if (this.state.screen === 'paused') {
            this.resume();
        }
    }

    onVisibility = () => {
        if (document.hidden) {
            this.pause();
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    toggleMute = (e) => {
        if (e?.currentTarget && typeof e.currentTarget.blur === 'function') {
            e.currentTarget.blur();
        }
        const muted = !this.state.muted;
        this.sound.setMuted(muted);
        util.set('arcade.muted', muted);
        this.setState({ muted });
    }

    tick = (dtMs) => {
        if (this.game) {
            step(this.game, this.held, dtMs);
        }
    }

    afterFrame = () => {
        if (!this.game) {
            return;
        }
        this.frame += 1;
        const events = this.game.events.splice(0);
        events.forEach(event => this.sound.play(event));
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, this.game, this.frame);
        }
        this.syncHud();
        if (this.game.status === 'gameOver') {
            this.loop.stop();
            this.onGameOver();
        }
    }

    syncHud() {
        const {
            score, wave, lives, status: engineStatus,
        } = this.game;
        if (score !== this.state.score || wave !== this.state.wave || lives !== this.state.lives
            || engineStatus !== this.state.engineStatus) {
            this.setState({
                score, wave, lives, engineStatus,
            });
        }
    }

    onGameOver = async () => {
        const { score, wave } = this.game;
        const durationMs = Date.now() - this.startedAt;
        this.setState({ screen: 'gameOver', submitting: score > 0 });
        const { arcadeScoreStore } = this.props.stores;
        if (score > 0 && arcadeScoreStore?.submitScore) {
            await arcadeScoreStore.submitScore({
                game: GAME_KEY, score, wave, durationMs,
            });
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
        if (!this.unmounted) {
            this.setState({ submitting: false });
        }
    }

    onKeyDown = (e) => {
        const { screen } = this.state;
        const control = CONTROL_KEYS[e.code];
        if (screen === 'game' && control) {
            this.held[control] = true;
            e.preventDefault();
            return;
        }
        if ((screen === 'game' || screen === 'paused') && e.code === 'KeyP' && !e.repeat) {
            this.togglePause();
            e.preventDefault();
            return;
        }
        if ((screen === 'title' || screen === 'gameOver') && KEY_START.includes(e.code) && !e.repeat) {
            this.startGame();
            e.preventDefault();
        }
    }

    onKeyUp = (e) => {
        const control = CONTROL_KEYS[e.code];
        if (control) {
            this.held[control] = false;
        }
    }

    hold = (control, value) => (e) => {
        if (e?.cancelable) {
            e.preventDefault();
        }
        if (!value || this.state.screen === 'game') {
            this.held[control] = value;
        }
    }

    renderButtons() {
        const button = (control, icon, kind = 'btn-outline-light') => (
            <button
                type='button'
                class={`btn ${kind} arcade-touch-btn`}
                onTouchStart={this.hold(control, true)}
                onTouchEnd={this.hold(control, false)}
                onTouchCancel={this.hold(control, false)}
                onMouseDown={this.hold(control, true)}
                onMouseUp={this.hold(control, false)}
                onMouseLeave={this.hold(control, false)}
            >
                <i class={`fa-solid ${icon}`} />
                {control === 'fire' && <span class='visually-hidden'><Text id='arcade.fire'>Fire</Text></span>}
                {control === 'thrust' && <span class='visually-hidden'><Text id='arcade.thrust'>Thrust</Text></span>}
            </button>
        );
        return (
            <div
                class='d-flex justify-content-between align-items-center w-100 mt-3 gap-2'
                style={`max-width: ${WIDTH * this.state.scale}px;`}
            >
                {button('left', 'fa-rotate-left')}
                {button('thrust', 'fa-angles-up')}
                {button('fire', 'fa-burst', 'btn-warning')}
                {button('right', 'fa-rotate-right')}
            </div>
        );
    }

    render() {
        const {
            screen, engineStatus, score, wave, lives, scale, isTouch, submitting, muted,
        } = this.state;
        const { arcadeScoreStore } = this.props.stores;
        const me = arcadeScoreStore?.leaderboardGame === GAME_KEY
            ? arcadeScoreStore.me : arcadeScoreStore?.bestByGame?.[GAME_KEY];
        const lastSubmit = arcadeScoreStore?.lastSubmit || null;
        const best = Math.max(me?.score || 0, lastSubmit?.best?.score || 0, score);
        const cssW = `${WIDTH * scale}px`;
        const cssH = `${HEIGHT * scale}px`;
        const inGame = screen === 'game' || screen === 'paused';

        return (
            <div
                class='d-flex flex-column align-items-center p-3 pb-5 bg-black text-white min-vh-100'
                ref={c => { this.wrapRef = c; }}
            >
                <div class='arcade-hud d-flex justify-content-between w-100 mb-2 small' style={`max-width: ${cssW};`}>
                    <span><Text id='arcade.score'>Score</Text> {score}</span>
                    <span><Text id='arcade.wave'>Wave</Text> {wave}</span>
                    <span><Text id='arcade.lives'>Lives</Text> {'▲'.repeat(Math.max(0, lives))}</span>
                    <span><Text id='arcade.best'>Best</Text> {best}</span>
                    <button type='button' class='btn btn-sm btn-outline-light py-0 px-2 lh-1' onClick={this.toggleMute}>
                        <i class={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                        <span class='visually-hidden'>
                            {muted ? <Text id='arcade.unmute'>Unmute</Text> : <Text id='arcade.mute'>Mute</Text>}
                        </span>
                    </button>
                </div>

                <div class='arcade-screen' style={`width: ${cssW}; height: ${cssH};`}>
                    <canvas
                        ref={c => { this.canvasRef = c; }}
                        width={WIDTH}
                        height={HEIGHT}
                        class='arcade-canvas'
                        style={`width: ${cssW}; height: ${cssH};`}
                    />

                    {screen === 'title' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.asteroids'>Asteroids</Text></h3>
                            <div class='small'>
                                {isTouch
                                    ? <Text id='arcade.controlsAsteroidsTouch'>Rotate · thrust · fire</Text>
                                    : <Text id='arcade.controlsAsteroidsKeyboard'>← → rotate · ↑ thrust · Space fires · P pauses</Text>}
                            </div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-light' onClick={this.startGame}>
                                    <Text id='arcade.start'>Start game</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                            <div class='small text-body-secondary'><Text id='arcade.insertCoin'>Press Space or tap Start</Text></div>
                        </div>
                    )}

                    {screen === 'game' && engineStatus === 'ready' && (
                        <div class='arcade-overlay'><Text id='arcade.ready'>Ready!</Text></div>
                    )}

                    {screen === 'game' && engineStatus === 'waveCleared' && (
                        <div class='arcade-overlay'>
                            <Text id='arcade.waveCleared' fields={{ wave }}>Wave {wave} cleared!</Text>
                        </div>
                    )}

                    {screen === 'paused' && (
                        <div class='arcade-overlay'>
                            <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-light btn-sm' onClick={this.resume}>
                                    <Text id='arcade.resume'>Resume</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light btn-sm' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {screen === 'gameOver' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.gameOver'>Game over</Text></h3>
                            <div><Text id='arcade.finalScore'>Final score</Text>: {score}</div>
                            {submitting && <div class='small'><Text id='arcade.savingScore'>Saving score…</Text></div>}
                            {!submitting && lastSubmit?.isNewBest && score > 0 && (
                                <div class='fw-bold'><Text id='arcade.newBest'>New personal best!</Text></div>
                            )}
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-light' onClick={this.startGame}>
                                    <Text id='arcade.playAgain'>Play again</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                                <button type='button' class='btn btn-outline-light' onClick={this.close}>
                                    <Text id='arcade.close'>Close</Text>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isTouch && inGame && this.renderButtons()}

                {inGame && !isTouch && (
                    <div class='d-flex gap-2 mt-3'>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.togglePause}>
                            {screen === 'paused'
                                ? <Text id='arcade.resume'>Resume</Text>
                                : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.close}>
                            <Text id='arcade.close'>Close</Text>
                        </button>
                    </div>
                )}

                {(screen === 'title' || screen === 'gameOver') && (
                    <div class='w-100' style={`max-width: ${cssW};`}>
                        <ArcadeLeaderboard stores={this.props.stores} game={GAME_KEY} />
                    </div>
                )}
            </div>
        );
    }
}

export default Asteroids;
